import { useCallback, useEffect, useMemo, useState } from 'react'
import type { SlashCommand } from '@/types/chat/SlashCommand'
import { createAvailableCommands } from './commands'

interface UseSlashCommandsProps {
  input: string
  onCommandExecute: (command: SlashCommand, args?: string) => void
  onWebSearchToggle?: () => void
  onDisplayMessage?: (message: {
    role: 'command'
    content: string
    timestamp: Date
  }) => void
  onSubmitMessage?: (message: string) => void
  onInputChange: (value: string) => void
}

export function useSlashCommands({
  input,
  onCommandExecute,
  onWebSearchToggle,
  onDisplayMessage,
  onSubmitMessage,
  onInputChange,
}: UseSlashCommandsProps) {
  const [selectedIndex, setSelectedIndex] = useState(0)
  const [isSearching, setIsSearching] = useState(false)

  const availableCommands = useMemo(
    () =>
      createAvailableCommands({
        onWebSearchToggle,
        onDisplayMessage,
        setIsSearching,
        onSubmitMessage,
      }),
    [onWebSearchToggle, onDisplayMessage, onSubmitMessage]
  )

  // Check if input starts with / to determine if dropdown should be shown
  const isCommandMode = input.startsWith('/') && !input.includes(' ')
  const searchQuery = isCommandMode ? input.slice(1).toLowerCase() : ''

  // Filter commands based on search query
  const filteredCommands = useMemo(() => {
    if (!isCommandMode) return []

    return availableCommands.filter((cmd) => {
      const matchesName = cmd.name.toLowerCase().includes(searchQuery)
      const matchesAlias = cmd.aliases?.some((alias) =>
        alias.toLowerCase().includes(searchQuery)
      )
      return matchesName || matchesAlias
    })
  }, [isCommandMode, searchQuery, availableCommands])

  // Reset selected index when filtered commands change
  useEffect(() => {
    setSelectedIndex(0)
  }, [])

  // Navigate dropdown with arrow keys
  const handleKeyDown = useCallback(
    (e: React.KeyboardEvent) => {
      if (!isCommandMode || filteredCommands.length === 0) return false

      if (e.key === 'ArrowDown') {
        e.preventDefault()
        e.stopPropagation() // Prevent global keyboard shortcuts from running
        setSelectedIndex((prev) => (prev + 1) % filteredCommands.length)
        return true
      }

      if (e.key === 'ArrowUp') {
        e.preventDefault()
        e.stopPropagation() // Prevent global keyboard shortcuts from running
        setSelectedIndex(
          (prev) =>
            (prev - 1 + filteredCommands.length) % filteredCommands.length
        )
        return true
      }

      if (e.key === 'Enter' && filteredCommands.length > 0) {
        e.preventDefault()
        const selectedCommand = filteredCommands[selectedIndex]
        if (selectedCommand) {
          // If command doesn't accept args, execute immediately
          if (selectedCommand.acceptsArgs === false) {
            onCommandExecute(selectedCommand)
          } else {
            // Otherwise, populate the input with the command name and a space
            // The user can then add arguments and press Enter again to execute
            onInputChange(`/${selectedCommand.name} `)
          }
        }
        return true
      }

      if (e.key === 'Escape') {
        e.preventDefault()
        e.stopPropagation() // Prevent global ESC handler from running
        return true // Signal to close/clear
      }

      return false
    },
    [isCommandMode, filteredCommands, selectedIndex, onInputChange, onCommandExecute, input]
  )

  const selectCommand = useCallback(
    (index: number) => {
      const command = filteredCommands[index]
      if (command) {
        // If command doesn't accept args, execute immediately
        if (command.acceptsArgs === false) {
          onCommandExecute(command)
        } else {
          // Otherwise, populate the input with the command name and a space
          // The user can then add arguments and press Enter to execute
          onInputChange(`/${command.name} `)
        }
      }
    },
    [filteredCommands, onCommandExecute, onInputChange]
  )

  return {
    isCommandMode,
    filteredCommands,
    availableCommands,
    selectedIndex,
    setSelectedIndex,
    handleKeyDown,
    selectCommand,
    isSearching,
  }
}
